import React, { useState, useRef, Ref, useEffect, useContext } from 'react';
import '../../App.css';
import './home.css';
import CSS from 'csstype';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images
import closeIcon from '../../icons/close.254.png';
import srcLogo from '../../icons/srcnet-dark.svg';
import srcLightLogo from '../../icons/srcnet-light.svg';

// types
import {
  API,
  APIStatus,
  APIStatusItem,
  ComputeServiceType,
  GatewayLogType,
  JobType,
  Site,
  ToolType,
} from '../../utils/types';
import { ComputeJob } from '../compute-jobs/types';
import { Event, EventType, EventTarget, APIStatusParams } from '../../utils/events';

// functions
import { APIPrefix, useDataManagementJobs } from '../../utils/functions';

// classes
import { useTranslation } from 'react-i18next';
import JobStatus from '../data-management/job-status';
import ActiveSessionsPanel from './active-compute-session/active-session-panel';
import ToolButton, { ToolButtonType } from '../../tools/tool-button';
import 'bootstrap/dist/css/bootstrap.min.css';
import ComputeJobsTable from './compute-jobs-table';
import StatusIndicator from '../../tools/status-indicator';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import { setShowNewSessionModal } from '../../app-slice';
import {
  API_INITIAL_STATUSES,
  API_STATUS_COLOURS,
  API_TITLES,
} from '../../utils/common/constants';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  Base row component for API status panel.
//
//  ------------------------------------------------------------

function BaseStatusPanelRow(props: {
  name: API | string;
  status?: APIStatus;
  showName?: boolean;
}) {
  //
  let indicatorColour = 'N';
  let fontWeight = '800';

  if (props.status) {
    indicatorColour = API_STATUS_COLOURS.get(props.status) || 'N';
    fontWeight = '400';
  }

  return (
    <div
      style={{
        flex: '0 0 auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: '2px',
      }}
    >
      <div style={{ flex: '0 0 0px' }} />
      <div className="home-service-status-detail" style={{ fontWeight: fontWeight }}>
        {props.showName === false ? '' : props.name}
      </div>
      <div style={{ flex: '1 1' }} />
      {indicatorColour !== 'N' && (
        <StatusIndicator status={APIStatus[props.status ?? APIStatus.UNKNOWN]} />
      )}
      <div
        style={{
          flex: '0 0 15px',
        }}
      />
    </div>
  );
  //
} // BaseStatusPanelRow

//  ------------------------------------------------------------
//
//  API status row component for API status panel.
//
//  ------------------------------------------------------------

function APIStatusRow(props: { name: string; status: APIStatus }) {
  //
  return <BaseStatusPanelRow name={props.name} status={props.status} showName={true} />;
  //
} // APIStatusRow

//  ------------------------------------------------------------
//
//  Heading and blank row component for API status panel.
//
//  ------------------------------------------------------------

function StatusPanelRow(props: { name: string; showName: boolean }) {
  //
  return <BaseStatusPanelRow name={props.name} showName={props.showName} />;
  //
} // StatusPanelRow

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function Home(props: {
  dataManagementJobs: JobType[];
  jobsLoading: boolean;
  logEntries: GatewayLogType[];
  logsFocusRow: React.MutableRefObject<string | undefined>;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // global state variables.
  const sG_SessionValid: boolean | undefined = useAppSelector(
    (state) => state.global.sessionValid,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_loggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );

  // home state variables.
  const sAPIStatuses: APIStatusItem[] = useAppSelector(
    (state) => state.home.sAPIStatuses,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //  ------------------------------------------------------------
  //
  //  Gets and updates statuses of all global APIs.
  //
  //  ------------------------------------------------------------

  const getAPIStatuses = async () => {
    //
    try {
      const apiResult = await fetch(APIPrefix() + '/v1/api_statuses', {
        credentials: 'include',
      });

      if (apiResult.status === 200) {
        const apiStatusItems: APIStatusItem[] = await apiResult.json();

        if (apiStatusItems) {
          const params: APIStatusParams = { apiStatuses: apiStatusItems };
          const newEvent: Event = {
            eventTarget: EventTarget.APP,
            eventType: EventType.API_STATUS_UPDATE,
            parameters: params,
          };
          dispatch(handleEvent({ event: newEvent }));
        }
      } else {
        console.log(
          `getAPIStatuses(): Received unexpected status code ${apiResult.status} ` +
            `from /api_statuses endpoint.`,
        );
      }
    } catch (e) {
      console.log(`getAPIStatuses(): Error getting API statuses: ${e}`);
    }

    if (!sG_SessionValid) {
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.LOAD_BACKEND_LOGS,
      };
      dispatch(handleEvent({ event: newEvent }));
    }
    //
  }; // getAPIStatuses

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  useEffect(() => {
    getAPIStatuses();
  }, [dispatch]);

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div
      role="region"
      aria-label="Home page"
      className="full-height-row gradient-background"
      style={{
        padding: '0px 5px 0px 0px',
        width: '100%',
        margin: '0px',
      }}
    >
      <div
        className="divider"
        data-divider="R"
        style={{
          padding: '0px',
          flex: '3 3',
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="divider"
          data-divider="B"
          style={{
            padding: '0px 0px 0px 0px',
            width: '100%',
            margin: '0px',
            display: 'flex',
            flexDirection: 'row',
            flex: '3 3',
          }}
        >
          <div
            className="divider"
            data-divider="R"
            style={{
              padding: '0px',
              backgroundColor: 'transparent',
              display: 'flex',
              flexDirection: 'row',
              flex: '2 2',
            }}
          >
            <div
              role="region"
              aria-labelledby="hdr-active-compute-sessions"
              className="panel-area"
              style={{
                backgroundColor: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1',
              }}
            >
              <div className="d-flex justify-content-between align-items-center panel-header">
                <div
                  id="hdr-active-compute-sessions"
                  role="heading"
                  aria-level={2}
                  className="header-text"
                  style={{ flex: '1 1' }}
                >
                  Active compute sessions
                </div>
                <ToolButton
                  key={t('Add')}
                  name="add-compute-session"
                  onClick={() => dispatch(setShowNewSessionModal({ show: true }))}
                  tooltip="Add a compute service session"
                  text={t('Add')}
                  type={ToolButtonType.PRIMARY}
                  disabled={sG_loggedIn !== true}
                />
              </div>
              <div className="active-session-wrapper">
                <ActiveSessionsPanel />
              </div>
            </div>
          </div>
          <div
            role="region"
            aria-labelledby="hdr-compute-jobs"
            className="panel-area"
            style={{
              paddingRight: '0px',
              backgroundColor: 'transparent',
              flex: '1 1',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              id="hdr-compute-jobs"
              role="heading"
              aria-level={2}
              className="header-text panel-header"
              style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}
            >
              Status of compute jobs
            </div>
            <ComputeJobsTable />
          </div>
        </div>
      </div>
      <div
        style={{
          flex: '1 1',
          paddingLeft: '0px',
          paddingRight: '0px',
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          role="region"
          aria-labelledby="lbl-data-staging-requests"
          className="home-service-right-content divider"
          data-divider="B"
          style={{
            backgroundColor: 'transparent',
            flex: '3 3',
          }}
        >
          <JobStatus
            dataManagementJobs={props.dataManagementJobs}
            jobsLoading={props.jobsLoading}
            logEntries={props.logEntries}
            logsFocusRow={props.logsFocusRow}
            transparentBackground={true}
          />
        </div>
        {/* ------------------------------------------------------------
        //
        //  Status Indicator Panel
        //
        //  ------------------------------------------------------------*/}
        <div
          role="region"
          aria-label="Status indicators"
          data-maximised="T"
          style={{
            width: '100%',
            marginTop: '0px',
            backgroundColor: 'transparent',
            flex: '2 2',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: '0 0 10px' }} />
          {/*
          //  Service statuses (IAM, Rucio)
          */}
          <StatusPanelRow name="Service status" showName={true} />
          {sAPIStatuses
            .filter((statusItem) => [API.IAM, API.RUCIO].includes(statusItem.api))
            .map((statusItem) => (
              <APIStatusRow
                name={API_TITLES.get(statusItem.api) || 'UNKNOWN'}
                status={statusItem.status}
              />
            ))}
          <StatusPanelRow name="gap" showName={false} />
          {/*
          // API statuses
          */}
          <StatusPanelRow name="API Status" showName={true} />
          {sAPIStatuses
            .filter((statusItem) => ![API.IAM, API.RUCIO].includes(statusItem.api))
            .map((statusItem) => (
              <APIStatusRow
                name={API_TITLES.get(statusItem.api) || 'UNKNOWN'}
                status={statusItem.status}
              />
            ))}
        </div>
      </div>
    </div>
  );
} // Home
